import { useEffect, useRef, type ReactNode } from 'react';

import { cn, FormUnsavedChangesStatus } from '@shigo-ui/ui';

export function DashboardPageFormActions({
  children,
  className,
  saveShortcutLabel,
}: {
  children: ReactNode;
  className?: string | undefined;
  saveShortcutLabel?: string | undefined;
}) {
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  const shortcut = isMac ? '⌘ Enter' : 'Ctrl Enter';
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const form = actionsRef.current?.closest('form');
    if (!form) return;
    function onKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        event.key !== 'Enter' ||
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        event.shiftKey ||
        event.isComposing
      )
        return;
      event.preventDefault();
      if (event.repeat) return;
      const submit = actionsRef.current?.querySelector<HTMLButtonElement>(
        'button[type="submit"]:not(:disabled)',
      );
      if (submit && form) form.requestSubmit(submit);
    }
    form.addEventListener('keydown', onKeyDown);
    return () => {
      form.removeEventListener('keydown', onKeyDown);
    };
  }, []);
  return (
    <div
      ref={actionsRef}
      className={cn(
        'border-border bg-card sticky bottom-0 z-20 mt-auto flex flex-wrap items-center gap-2 border-t px-5 py-4',
        className,
      )}
    >
      <FormUnsavedChangesStatus />
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <kbd
          title={saveShortcutLabel ?? shortcut}
          aria-label={saveShortcutLabel ?? shortcut}
          className="text-muted-foreground mr-3 hidden text-xs font-normal md:block"
        >
          {shortcut}
        </kbd>
        {children}
      </div>
    </div>
  );
}
